/**
 * RecordPage — Full trip report recording experience
 *
 * Two input modes:
 *   1. Voice recording (default) — uses AudioRecorder component
 *   2. Text input — large textarea for typing/pasting transcript
 *
 * Processing pipeline:
 *   Step 1: Recording/typing (user action)
 *   Step 2: "Transcribing..." (POST /api/copilot/transcribe) — voice only
 *   Step 3: "Extracting..." (POST /api/copilot/trip-reports/extract)
 *   Step 4: Navigate to review screen with TripReportDraft
 *
 * Error handling:
 *   - Mic permission denied → message with settings link
 *   - Transcription failure → retry or switch to text input
 *   - Network error → save audio locally, retry when online (basic)
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mic, Type, Pencil, ClipboardPaste, AlertCircle, RefreshCw, Loader2, Check, Clock } from 'lucide-react';
import type { AxiosError } from 'axios';
import { cn } from '../lib/utils';
import { AudioRecorder } from '../components/AudioRecorder';
import { TripReportCard } from '../components/TripReportCard';
import { type AudioRecordingResult } from '../hooks/useAudioRecording';
import { useRecentTripReports } from '../hooks/useApi';
import { reportToDraft } from '../utils/reportToDraft';
import type { TripReport } from '../types';
import {
  transcribeAudio,
  extractTripReport,
  type TripReportDraft,
} from '../services/copilotApi';

import {
  type InputMode,
  type ProcessingStep,
  STEPS,
  getActiveStepIndex,
  getVisibleSteps,
} from './recordStepUtils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ProcessingError {
  step: 'transcribing' | 'extracting';
  message: string;
  canRetry: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Extract the human-readable error message from an Axios or generic error. */
function extractErrorMessage(err: unknown, fallback: string): string {
  // Axios errors carry the backend detail in response.data
  const axErr = err as AxiosError<{ detail?: string }>;
  if (axErr?.response?.data?.detail) {
    return axErr.response.data.detail;
  }
  if (err instanceof Error) {
    return err.message;
  }
  return fallback;
}

// ---------------------------------------------------------------------------
// StepIndicator component
// ---------------------------------------------------------------------------

function StepIndicator({
  currentStep,
  mode,
  error,
}: {
  currentStep: ProcessingStep;
  mode: InputMode;
  error: ProcessingError | null;
}) {
  const activeIdx = getActiveStepIndex(currentStep, mode);

  const visibleSteps = getVisibleSteps(mode);

  return (
    <div className="flex items-center justify-center gap-1 px-4 py-3">
      {visibleSteps.map((step, visIdx) => {
        // Map back to original STEPS index for active/completed logic
        const origIdx = STEPS.findIndex((s) => s.key === step.key);
        const isActive = origIdx === activeIdx;
        const isCompleted = origIdx < activeIdx;
        const hasError = error && (
          (error.step === 'transcribing' && step.key === 'transcribing') ||
          (error.step === 'extracting' && step.key === 'extracting')
        );

        return (
          <div key={step.key} className="flex items-center gap-1">
            {visIdx > 0 && (
              <div
                className={cn(
                  'h-0.5 w-6',
                  isCompleted ? 'bg-brand' : 'bg-border',
                )}
              />
            )}
            <div className="flex flex-col items-center gap-0.5">
              <div
                className={cn(
                  'flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold transition-colors',
                  hasError
                    ? 'bg-error text-white'
                    : isCompleted
                      ? 'bg-brand text-white'
                      : isActive
                        ? 'bg-brand/20 text-brand ring-2 ring-brand'
                        : 'bg-surface-inset text-text-muted',
                )}
              >
                {hasError ? (
                  <AlertCircle className="h-3.5 w-3.5" />
                ) : isCompleted ? (
                  <Check className="h-3.5 w-3.5" />
                ) : isActive && (currentStep === 'transcribing' || currentStep === 'extracting') ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  visIdx + 1
                )}
              </div>
              <span
                className={cn(
                  'text-xs font-medium leading-none',
                  isActive ? 'text-brand' : 'text-text-muted',
                )}
              >
                {step.label}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ModeToggle component
// ---------------------------------------------------------------------------

function ModeToggle({
  mode,
  onChange,
  disabled,
}: {
  mode: InputMode;
  onChange: (m: InputMode) => void;
  disabled: boolean;
}) {
  return (
    <div className="flex items-center justify-center gap-1 rounded-full bg-surface-inset p-1">
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange('voice')}
        className={cn(
          'flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium transition-colors',
          mode === 'voice'
            ? 'bg-brand text-white shadow-sm'
            : 'text-text-secondary active:text-text-primary',
          disabled && 'opacity-50 cursor-not-allowed',
        )}
      >
        <Mic className="h-4 w-4" />
        Voice
      </button>
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange('text')}
        className={cn(
          'flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium transition-colors',
          mode === 'text'
            ? 'bg-brand text-white shadow-sm'
            : 'text-text-secondary active:text-text-primary',
          disabled && 'opacity-50 cursor-not-allowed',
        )}
      >
        <Type className="h-4 w-4" />
        Text
      </button>
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange('edit')}
        className={cn(
          'flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium transition-colors',
          mode === 'edit'
            ? 'bg-brand text-white shadow-sm'
            : 'text-text-secondary active:text-text-primary',
          disabled && 'opacity-50 cursor-not-allowed',
        )}
      >
        <Pencil className="h-4 w-4" />
        Edit
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ProcessingOverlay component
// ---------------------------------------------------------------------------

function ProcessingOverlay({ step, startedAt }: { step: 'transcribing' | 'extracting'; startedAt: number }) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const tick = () => setElapsed(Math.floor((Date.now() - startedAt) / 1000));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [startedAt]);

  const label = step === 'transcribing' ? 'Transcribing...' : 'Extracting details...';

  // Progressive messages to reassure the user
  let sublabel: string;
  if (step === 'transcribing') {
    if (elapsed < 15) sublabel = 'Converting your recording to text';
    else if (elapsed < 45) sublabel = 'Still processing — longer recordings take more time';
    else sublabel = 'Almost there — your assistant is finishing up';
  } else {
    if (elapsed < 10) sublabel = 'Your assistant is identifying key details from your report';
    else if (elapsed < 30) sublabel = 'Analyzing topics, action items, and contacts';
    else sublabel = 'Still working — complex reports take a moment';
  }

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m > 0 ? `${m}:${sec.toString().padStart(2, '0')}` : `${sec}s`;
  };

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6">
      <Loader2 className="h-12 w-12 animate-spin text-brand" />
      <div className="text-center">
        <p className="text-lg font-semibold text-text-primary">{label}</p>
        <p className="mt-1 text-sm text-text-secondary">{sublabel}</p>
        <div className="mt-3 flex items-center justify-center gap-1.5 text-xs text-text-muted">
          <Clock className="h-3 w-3" />
          <span>{formatTime(elapsed)}</span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ErrorDisplay component
// ---------------------------------------------------------------------------

function ErrorDisplay({
  error,
  onRetry,
  onSwitchToText,
}: {
  error: ProcessingError;
  onRetry: () => void;
  onSwitchToText: () => void;
}) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-error/10">
        <AlertCircle className="h-8 w-8 text-error" />
      </div>
      <div className="text-center">
        <p className="text-lg font-semibold text-text-primary">
          {error.step === 'transcribing' ? 'Transcription Failed' : 'Extraction Failed'}
        </p>
        <p className="mt-1 max-w-xs text-sm text-text-secondary">{error.message}</p>
      </div>
      <div className="flex flex-col gap-2 w-full max-w-xs">
        {error.canRetry && (
          <button
            type="button"
            onClick={onRetry}
            className={cn(
              'flex h-touch items-center justify-center gap-2 rounded-lg bg-brand',
              'font-semibold text-white transition-colors active:bg-brand-700',
            )}
          >
            <RefreshCw className="h-4 w-4" />
            Try Again
          </button>
        )}
        {error.step === 'transcribing' && (
          <button
            type="button"
            onClick={onSwitchToText}
            className={cn(
              'flex h-touch items-center justify-center gap-2 rounded-lg',
              'border border-border font-semibold text-text-primary',
              'transition-colors active:bg-surface-inset',
            )}
          >
            <Type className="h-4 w-4" />
            Type Instead
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TextInput component
// ---------------------------------------------------------------------------

function TextInput({
  value,
  onChange,
  onSubmit,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  disabled: boolean;
}) {
  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        onChange(text);
      }
    } catch {
      // Clipboard API may be unavailable or denied
    }
  };

  return (
    <div className="flex flex-1 flex-col gap-3 px-4 py-4">
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder="Type or paste your trip report transcript here..."
        className={cn(
          'min-h-[200px] flex-1 resize-none rounded-lg border border-border bg-surface p-4',
          'text-sm text-text-primary placeholder:text-text-muted',
          'focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20',
          disabled && 'opacity-50 cursor-not-allowed',
        )}
      />

      <div className="flex gap-2">
        <button
          type="button"
          onClick={handlePaste}
          disabled={disabled}
          className={cn(
            'flex h-touch flex-1 items-center justify-center gap-2 rounded-lg',
            'border border-border text-sm font-medium text-text-secondary',
            'transition-colors active:bg-surface-inset',
            disabled && 'opacity-50 cursor-not-allowed',
          )}
        >
          <ClipboardPaste className="h-4 w-4" />
          Paste from clipboard
        </button>
      </div>

      <button
        type="button"
        onClick={onSubmit}
        disabled={disabled || !value.trim()}
        className={cn(
          'flex h-touch items-center justify-center rounded-lg bg-brand',
          'font-semibold text-white transition-colors active:bg-brand-700',
          'disabled:opacity-50 disabled:cursor-not-allowed',
        )}
      >
        Extract Trip Report
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// EditReportList — shows existing trip reports for editing
// ---------------------------------------------------------------------------

function EditReportList({ onEdit }: { onEdit: (report: TripReport) => void }) {
  const { reports, loading, error } = useRecentTripReports(50);

  // Only show mobile-submitted reports (chunk-sourced have no PUT target)
  const editableReports = reports.filter(
    (r) => (r as unknown as { source?: string }).source !== 'chunk',
  );

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-brand" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
        <AlertCircle className="h-8 w-8 text-error" />
        <p className="mt-2 text-sm text-text-secondary">Failed to load reports</p>
      </div>
    );
  }

  if (editableReports.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
        <Pencil className="h-8 w-8 text-text-muted" />
        <p className="mt-3 text-sm font-medium text-text-primary">No reports to edit</p>
        <p className="mt-1 text-xs text-text-muted">
          Record a trip report first, then come back here to edit it.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col overflow-y-auto px-4 pb-4">
      <p className="mb-3 text-xs text-text-muted">
        Tap a report to edit it. Changes update the database and search index.
      </p>
      {editableReports.map((report) => (
        <div key={report.id} className="mb-2">
          <TripReportCard
            report={report}
            onEdit={() => onEdit(report)}
          />
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// RecordPage
// ---------------------------------------------------------------------------

export function RecordPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<InputMode>('voice');
  const [step, setStep] = useState<ProcessingStep>('idle');
  const [error, setError] = useState<ProcessingError | null>(null);
  const [textInput, setTextInput] = useState('');

  // Stash recording result for retry
  const lastRecordingRef = useRef<AudioRecordingResult | null>(null);
  // Stash transcript for retry
  const lastTranscriptRef = useRef<string | null>(null);
  // Track when the current processing step started (for elapsed timer)
  const processingStartRef = useRef<number>(Date.now());

  const isProcessing = step === 'transcribing' || step === 'extracting';

  /**
   * Run the extraction pipeline on a transcript and navigate to review.
   */
  const runExtraction = useCallback(
    async (transcript: string) => {
      setStep('extracting');
      setError(null);
      processingStartRef.current = Date.now();
      lastTranscriptRef.current = transcript;

      try {
        const draft: TripReportDraft = await extractTripReport(transcript);
        setStep('done');
        // Navigate to review screen, passing the draft via state
        navigate('/copilot/review', { state: { draft } });
      } catch (err: unknown) {
        const msg = extractErrorMessage(err, 'Something went wrong during extraction.');
        setError({
          step: 'extracting',
          message: msg,
          canRetry: true,
        });
        setStep('idle');
      }
    },
    [navigate],
  );

  /**
   * Run the full pipeline: transcribe audio → extract.
   */
  const runTranscription = useCallback(
    async (recording: AudioRecordingResult) => {
      setStep('transcribing');
      setError(null);
      processingStartRef.current = Date.now();
      lastRecordingRef.current = recording;

      try {
        const result = await transcribeAudio(recording.blob, recording.mimeType);
        if (!result.transcript.trim()) {
          setError({
            step: 'transcribing',
            message: 'No speech was detected in the recording. Try again or type your report.',
            canRetry: true,
          });
          setStep('idle');
          return;
        }
        // Proceed to extraction
        await runExtraction(result.transcript);
      } catch (err: unknown) {
        const msg = extractErrorMessage(err, 'Transcription failed. Please try again.');

        // Try to save audio locally for offline retry
        try {
          saveAudioLocally(recording);
        } catch {
          // Best-effort local save
        }

        setError({
          step: 'transcribing',
          message: msg,
          canRetry: true,
        });
        setStep('idle');
      }
    },
    [runExtraction],
  );

  /**
   * Handle completed voice recording — start pipeline.
   */
  const handleRecordingComplete = useCallback(
    (result: AudioRecordingResult) => {
      runTranscription(result);
    },
    [runTranscription],
  );

  /**
   * Handle text submission — skip transcription, go straight to extraction.
   */
  const handleTextSubmit = useCallback(() => {
    const transcript = textInput.trim();
    if (!transcript) return;
    runExtraction(transcript);
  }, [textInput, runExtraction]);

  /**
   * Retry the last failed step.
   */
  const handleRetry = useCallback(() => {
    if (!error) return;
    if (error.step === 'transcribing' && lastRecordingRef.current) {
      runTranscription(lastRecordingRef.current);
    } else if (error.step === 'extracting' && lastTranscriptRef.current) {
      runExtraction(lastTranscriptRef.current);
    }
  }, [error, runTranscription, runExtraction]);

  /**
   * Switch from voice to text mode (e.g. after transcription failure).
   */
  const handleSwitchToText = useCallback(() => {
    setMode('text');
    setError(null);
    setStep('idle');
  }, []);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  // Edit mode: handler to open a report for editing
  const handleEditReport = useCallback(
    (report: TripReport) => {
      const draft = reportToDraft(report);
      navigate('/copilot/review', { state: { draft } });
    },
    [navigate],
  );

  return (
    <div className="flex flex-1 flex-col">
      {/* Step indicator — visible in voice/text modes only */}
      {mode !== 'edit' && (
        <StepIndicator currentStep={step} mode={mode} error={error} />
      )}

      {/* Mode toggle — visible only when idle */}
      {step === 'idle' && !error && (
        <div className="flex justify-center pb-2">
          <ModeToggle mode={mode} onChange={setMode} disabled={isProcessing} />
        </div>
      )}

      {/* Error state */}
      {error ? (
        <ErrorDisplay
          error={error}
          onRetry={handleRetry}
          onSwitchToText={handleSwitchToText}
        />
      ) : isProcessing ? (
        /* Processing overlay */
        <ProcessingOverlay step={step as 'transcribing' | 'extracting'} startedAt={processingStartRef.current} />
      ) : mode === 'edit' ? (
        /* Edit existing reports mode */
        <EditReportList onEdit={handleEditReport} />
      ) : mode === 'voice' ? (
        /* Voice recording mode */
        <div className="flex flex-1 flex-col items-center justify-center px-6">
          <AudioRecorder
            onRecordingComplete={handleRecordingComplete}
            maxDuration={600}
            fallback={
              <div className="flex flex-col items-center gap-4 text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-warning/10">
                  <AlertCircle className="h-8 w-8 text-warning" />
                </div>
                <p className="text-sm text-text-secondary">
                  Audio recording is not supported in this browser.
                </p>
                <button
                  type="button"
                  onClick={() => setMode('text')}
                  className={cn(
                    'flex h-touch items-center gap-2 rounded-lg border border-border px-6',
                    'font-medium text-text-primary transition-colors active:bg-surface-inset',
                  )}
                >
                  <Type className="h-4 w-4" />
                  Use Text Input
                </button>
              </div>
            }
          />
          <p className="mt-4 max-w-xs text-center text-xs text-text-muted">
            Tap to record your trip report (up to 10 min). Your assistant will transcribe and extract key details.
          </p>
        </div>
      ) : (
        /* Text input mode */
        <TextInput
          value={textInput}
          onChange={setTextInput}
          onSubmit={handleTextSubmit}
          disabled={isProcessing}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Save audio blob to localStorage as a base64 string for offline retry.
 * This is a basic implementation — stores the most recent recording only.
 */
function saveAudioLocally(recording: AudioRecordingResult): void {
  const reader = new FileReader();
  reader.onloadend = () => {
    try {
      const data = {
        audio: reader.result as string,
        mimeType: recording.mimeType,
        duration: recording.duration,
        savedAt: new Date().toISOString(),
      };
      localStorage.setItem('copilot_pending_recording', JSON.stringify(data));
    } catch {
      // localStorage might be full — ignore
    }
  };
  reader.readAsDataURL(recording.blob);
}
